// Browser-side WebRTC peer. The CLI is the offerer (host); we are the answerer.
//
// The signaling Worker forwards JSON messages verbatim between the two peers:
//   { type:"sdp", sdp:RTCSessionDescriptionInit }
//   { type:"ice", candidate:RTCIceCandidateInit | null }
//   { type:"peer-joined" }   from server, host side only
//   { type:"peer-left" }     from server
//   { type:"ready" }         from host, after data channel open
//   { type:"bye" }

import { SignalingClient } from "./signal";

const ICE_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.cloudflare.com:3478" },
  { urls: "stun:stun.l.google.com:19302" },
];

export interface PeerHandle {
  pc: RTCPeerConnection;
  channel: Promise<RTCDataChannel>;
  closed: Promise<void>;
}

export function connectPeer(signal: SignalingClient): PeerHandle {
  const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
  const pendingRemoteCandidates: RTCIceCandidateInit[] = [];
  let signalQueue = Promise.resolve();

  // Host creates the data channel; we receive it.
  const channel = new Promise<RTCDataChannel>((resolve) => {
    pc.addEventListener("datachannel", (e) => {
      const dc = e.channel;
      if (dc.readyState === "open") resolve(dc);
      else dc.addEventListener("open", () => resolve(dc), { once: true });
    });
  });

  const closed = new Promise<void>((resolve) => {
    pc.addEventListener("connectionstatechange", () => {
      if (
        pc.connectionState === "closed" ||
        pc.connectionState === "failed" ||
        pc.connectionState === "disconnected"
      ) resolve();
    });
  });

  pc.addEventListener("icecandidate", (e) => {
    signal.send({ type: "ice", candidate: e.candidate ? e.candidate.toJSON() : null });
  });

  async function addRemoteCandidate(cand: RTCIceCandidateInit): Promise<void> {
    if (!pc.remoteDescription) {
      pendingRemoteCandidates.push(cand);
      return;
    }
    await pc.addIceCandidate(cand);
  }

  async function flushRemoteCandidates(): Promise<void> {
    while (pendingRemoteCandidates.length > 0 && pc.remoteDescription) {
      const cand = pendingRemoteCandidates.shift();
      if (!cand) continue;
      try { await pc.addIceCandidate(cand); } catch { /* ignore stale candidates */ }
    }
  }

  async function handleSignalMessage(msg: { type: string; [k: string]: unknown }): Promise<void> {
    if (msg.type === "sdp") {
      const desc = msg.sdp as RTCSessionDescriptionInit;
      await pc.setRemoteDescription(desc);
      await flushRemoteCandidates();
      if (desc.type === "offer") {
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        signal.send({ type: "sdp", sdp: pc.localDescription });
      }
    } else if (msg.type === "ice") {
      const cand = (msg as unknown as { candidate: RTCIceCandidateInit | null }).candidate;
      if (cand) {
        try { await addRemoteCandidate(cand); } catch { /* ignore stale candidates */ }
      }
    } else if (msg.type === "peer-left") {
      pc.close();
    }
  }

  signal.onMessage((msg) => {
    signalQueue = signalQueue
      .then(() => handleSignalMessage(msg))
      .catch((err) => {
        console.error("signaling message failed", err);
      });
  });

  return { pc, channel, closed };
}
